import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Users, PlayCircle, PauseCircle, Coffee, LogOut, Clock, RotateCw } from "lucide-react";

export default function StaffAvailability() {
  const { user } = useAuth();
  const isOwner = (user?.account_type || user?.role) === "business_owner";
  const [snap, setSnap] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        api.get("/staff/availability"),
        api.get("/staff/attendance?limit=80"),
      ]);
      setSnap(s.data);
      setHistory(h.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const forceClockOut = async (uid) => {
    try {
      await api.post(`/staff/${uid}/force-clock-out`);
      toast.success("Closed shift");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !snap) return <div className="text-slate-500 p-6">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="staff-availability-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-[#1B4332]" />
            Who&apos;s on the clock
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            New inquiries, bookings, and incidents are routed to a clocked-in staff member
            with the right permissions. If nobody&apos;s available, the owner gets the fallback.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="staff-refresh-btn"
          className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center gap-1"
        >
          <RotateCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3" data-testid="staff-counts">
        <StatCard label="Online now" value={snap.online_count} tone="emerald" testId="staff-online-count" />
        <StatCard label="Offline" value={snap.offline_count} tone="slate" testId="staff-offline-count" />
        <StatCard label="Team size" value={snap.total} tone="blue" testId="staff-total-count" />
      </div>

      <Section title="Online" items={snap.online} isOwner={isOwner} onForceOut={forceClockOut} testId="staff-online-list" />
      <Section title="Offline" items={snap.offline} isOwner={isOwner} testId="staff-offline-list" />

      {isOwner && (
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="staff-history">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-500" /> Recent attendance history
          </h2>
          <p className="text-xs text-slate-500 mt-1">Last 80 shifts across all staff.</p>
          {history.length === 0 ? (
            <div className="text-sm text-slate-500 mt-3" data-testid="staff-history-empty">No shifts recorded yet.</div>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-3">Employee</th>
                    <th className="py-2 pr-3">Clock in</th>
                    <th className="py-2 pr-3">Clock out</th>
                    <th className="py-2 pr-3">Duration</th>
                    <th className="py-2">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.map((s) => (
                    <tr key={s.id} data-testid={`staff-history-row-${s.id}`}>
                      <td className="py-2 pr-3 font-medium">{s.user_name}</td>
                      <td className="py-2 pr-3 text-slate-700">{formatDT(s.clock_in_at)}</td>
                      <td className="py-2 pr-3 text-slate-700">{s.clock_out_at ? formatDT(s.clock_out_at) : <span className="text-emerald-700 font-medium">Open</span>}</td>
                      <td className="py-2 pr-3 text-slate-700">{durationText(s.clock_in_at, s.clock_out_at)}</td>
                      <td className="py-2 text-slate-500 capitalize">{(s.closed_reason || "-").replace(/_/g, " ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone, testId }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    slate:   "border-slate-200 bg-white text-slate-800",
    blue:    "border-sky-200 bg-sky-50 text-sky-900",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone] || tones.slate}`} data-testid={testId}>
      <div className="text-xs uppercase tracking-wider opacity-80 font-semibold">{label}</div>
      <div className="font-display text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}

function Section({ title, items, isOwner, onForceOut, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid={testId}>
      <h2 className="font-display text-lg font-semibold">{title} <span className="text-slate-400 text-sm">({items.length})</span></h2>
      {items.length === 0 ? (
        <div className="text-sm text-slate-500 mt-3">Nobody {title === "Online" ? "clocked in" : "clocked out"} right now.</div>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100">
          {items.map((e) => (
            <li key={e.user_id} className="py-3 flex flex-wrap items-center justify-between gap-2" data-testid={`staff-row-${e.user_id}`}>
              <div className="min-w-0">
                <div className="font-medium">
                  {e.name}
                  <span className="text-xs text-slate-500 ml-2">{e.title || ""}</span>
                </div>
                <div className="text-xs text-slate-500">
                  {e.clocked_in ? (
                    <>
                      <StatusPill status={e.status} />
                      <span className="ml-2">Clocked in {timeAgo(e.clock_in_at)}</span>
                    </>
                  ) : (
                    <span>
                      Last seen {e.last_clock_in_at ? timeAgo(e.last_clock_in_at) : "-"}
                    </span>
                  )}
                </div>
              </div>
              {isOwner && e.clocked_in && (
                <button
                  type="button"
                  onClick={() => onForceOut(e.user_id)}
                  data-testid={`staff-force-out-${e.user_id}`}
                  className="text-xs px-2 py-1 rounded border border-rose-200 text-rose-700 bg-white hover:bg-rose-50 inline-flex items-center gap-1"
                >
                  <LogOut className="w-3 h-3" /> Force clock-out
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    available: { c: "bg-emerald-100 text-emerald-800", icon: PlayCircle },
    busy:      { c: "bg-amber-100 text-amber-800", icon: PauseCircle },
    on_break:  { c: "bg-slate-100 text-slate-700", icon: Coffee },
  };
  const m = map[status] || map.available;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${m.c}`}>
      <Icon className="w-3 h-3" /> {(status || "available").replace("_", " ")}
    </span>
  );
}

function formatDT(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function timeAgo(iso) {
  if (!iso) return "-";
  try {
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const h = Math.floor(mins / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return iso; }
}

function durationText(start, end) {
  if (!start) return "-";
  try {
    const s = new Date(start).getTime();
    const e = end ? new Date(end).getTime() : Date.now();
    const mins = Math.max(0, Math.floor((e - s) / 60000));
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m}m`;
    return `${h}h${m ? ` ${m}m` : ""}`;
  } catch { return "-"; }
}
